import { useCallback, useContext } from "react";

import { CampaignMessageType, CAMPAIGN_MESSAGE_TYPE_OPTIONS } from "@regal-voice/shared-types/entities";
import { Divider } from "antd";
import { RuleObject } from "antd/lib/form";

import { RVFormFloatingLabelWrap } from "@regal-voice/ui";
import { Column } from "Components/shared/Flexbox";
import { Campaign, CampaignType } from "Types/Campaign";

import { JourneyFormContext } from "../../../../../shared/Contexts/JourneyFormContext";
import { CampaignLink } from "../../Shared/CampaignLink/CampaignLink";
import { CampaignSelect } from "../../Shared/CampaignSelect";
import { getCampaignSMSContent, getCampaignType } from "../../Shared/helpers";

import styles from "./CallFormItems.module.scss";

export type ActionFormItemsProps = {
    callType: CampaignMessageType;
    showLabel?: boolean;
    showCampaignEditLink?: boolean;
    formValues?: { campaign?: Campaign };
    data?: { campaigns: Campaign[] };
    getLinkProps: (callType: CampaignMessageType, id?: string) => { linkTo: string; linkText: string };
};

const campaignErrorMessage = "Campaign is required";

export function ActionFormItems({
    callType = CAMPAIGN_MESSAGE_TYPE_OPTIONS.sms,
    showLabel = true,
    showCampaignEditLink = true,
    formValues,
    data,
    getLinkProps,
}: ActionFormItemsProps): JSX.Element {
    const { formSubmited } = useContext(JourneyFormContext);
    const campaign: Partial<Campaign> = formValues?.campaign || {};
    const campaigns = data?.campaigns || [];

    const validateNotEmpty = useCallback(
        (_: RuleObject, value: string): Promise<void> => {
            if (!formSubmited) {
                return Promise.resolve();
            }
            if (!value || Object.keys(value).length === 0) {
                return Promise.reject(new Error(campaignErrorMessage));
            }
            return Promise.resolve();
        },
        [formSubmited]
    );

    // link to create/edit campaign
    // open campaign edit page with default values corresponding to the call type
    const { linkTo, linkText } = getLinkProps(callType, campaign?.id);

    return (
        <Column gap={4}>
            <Column gap={2}>
                {showLabel && <div className={styles.selectLabel}>Select Campaign</div>}
                <RVFormFloatingLabelWrap
                    name="campaign"
                    rules={[
                        {
                            validator: validateNotEmpty,
                        },
                    ]}
                    label={"Name"}
                    extra={showCampaignEditLink && <CampaignLink to={linkTo} text={linkText} />}
                >
                    <CampaignSelect data={campaigns} callType={callType} data-testid="campaign-select" />
                </RVFormFloatingLabelWrap>
            </Column>
            <SMSContentPreview id={campaign?.id} data={data} />
        </Column>
    );
}

export type SMSContentPreviewProps = {
    id?: string;
    data?: { campaigns: Campaign[] };
};

function SMSContentPreview({ id, data }: SMSContentPreviewProps): JSX.Element {
    const campaigns = data?.campaigns || [];
    const type = id ? getCampaignType(campaigns, id) : "";
    const smsContent = id ? getCampaignSMSContent(campaigns, id) : "";

    return (
        <>
            {smsContent && <Divider className={styles.divider} />}
            {smsContent && (
                <Column gap={2}>
                    <span>
                        {type === CampaignType.AGENT_TASK
                            ? "Preview of SMS Task Instructions:"
                            : "Preview of SMS Content:"}
                    </span>
                    <Column padding={3} className={styles.smsPreview}>
                        <span className={styles.previewMessage}>{smsContent}</span>
                    </Column>
                </Column>
            )}
        </>
    );
}
